import React from 'react'
import {useIntl} from 'react-intl'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'
import {Size} from 'types/Theme'

import {Icon} from '@vio/atlas/Icon'
import {cssTheme} from '@vio/atlas/themes'
import {linariaMq} from '@vio/atlas/utils/breakpoints'
import minus from '@vio/atlas/utils/minus'
import {
  CurrencyDisplayVariant,
  formatAbbreviatedPriceValue,
  formatPriceValue
} from '@vio/core/src/price/business/formatPrice'
import {RoundingStrategy} from '@vio/core/src/utils/number'

export type PriceSize = Exclude<Size, 'xs' | 'xxl'>

interface Props {
  /** Pass through classname to allow styles overrides */
  className?: string
  /** The currency code for the price */
  currency?: string
  /** Whether the price should be styled as an anchor price */
  isAnchor?: boolean

  /** Whether the price is a private deal */
  isPrivate?: boolean
  /** Whether the price is an app lock deal */
  isAppLocked?: boolean
  /** Price maximum decimal places */
  maximumFractionDigits?: number
  /** Price minimum decimal places */
  minimumFractionDigits?: number
  /** Whether the price should not be styled */
  noStyle?: boolean
  /** How the price should be rounded  */
  roundingStrategy?: RoundingStrategy
  /** Whether the app locked icon can be shown. It is only shown when isAppLocked is also true. */
  showAppIcon?: boolean
  /** Price size */
  size?: PriceSize
  /** The value to be displayed as the price */
  value: number
  /** Whether the price should be displayed in offer */
  shouldAlignOffer?: boolean

  /** When true it will abbreviate the prices to max 6 characters, e.g. €12.3k or €123M. Note that k and M are not translated */
  isAbbreviated?: boolean
  /** When true it will abbreviate the currency symbol if it exists in ShortCurrencySymbols, otherwise it will not show the currency symbol. This only works if isAbbreviated is also true */
  useShortCurrency?: boolean
  /** Identify the element for selection in integration tests, FullStory, etc. */
  dataId?: string
  /** How to display the currency in currency formatting. i.e. 'symbol', 'narrowSymbol', 'code', 'name', */
  currencyDisplayVariant?: CurrencyDisplayVariant
  /** Style prop is used by Linaria to pass CSS variables in cases when we need to style custom components in this way: `const StyledPrice = styled(Price)` */
  style?: React.CSSProperties
  /** Font weight variant for the price display */
  variant?: 'bold' | 'regular'
}

const StandardPriceElement = styled.div`
  display: inline-block;
  color: ${cssTheme.colors.price.default};
  &.--special {
    color: ${cssTheme.colors.price.special};
  }
  &.--text-sm-regular {
    ${cssTheme.typography.bodyS}
  }
  &.--text-sm-bold {
    ${cssTheme.typography.titleS}
  }
  &.--text-md-regular {
    ${cssTheme.typography.bodyM}
  }
  &.--text-md-bold {
    ${cssTheme.typography.titleM}
  }
  &.--text-lg-regular {
    ${cssTheme.typography.bodyL}
  }
  &.--text-lg-bold {
    ${cssTheme.typography.titleL}
  }
  &.--text-xl-regular,
  &.--text-xl-bold {
    ${cssTheme.typography.titleXL}
  }
`

const AnchorPriceElement = styled.div`
  color: ${cssTheme.colors.price.anchor};
  text-decoration: line-through;
  ${cssTheme.typography.bodyXS}
  ${linariaMq.desktopXs} {
    ${cssTheme.typography.bodyS}
  }
`
const PriceWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${cssTheme.layout.spacing.s200};

  &.--with-margin-bottom {
    margin-bottom: ${minus(cssTheme.layout.spacing.s100)};
  }
`

export const Price = ({
  className,
  isAnchor = false,
  value,
  currency,
  noStyle = false,
  minimumFractionDigits,
  maximumFractionDigits,
  isPrivate = false,
  roundingStrategy,
  isAppLocked = false,
  showAppIcon = false,
  size = 'lg',
  shouldAlignOffer = false,
  isAbbreviated = false,
  useShortCurrency = false,
  dataId,
  currencyDisplayVariant,
  style,
  variant = 'bold'
}: Props) => {
  const {locale} = useIntl()
  const formattedNumber = isAbbreviated
    ? formatAbbreviatedPriceValue({
        locale,
        roundingStrategy,
        value,
        currency,
        useShortCurrency,
        currencyDisplayVariant,
        maximumFractionDigits
      })
    : formatPriceValue({
        locale,
        roundingStrategy,
        value,
        currency,
        minimumFractionDigits: minimumFractionDigits ?? 0,
        maximumFractionDigits: maximumFractionDigits ?? 0,
        currencyDisplayVariant
      })
  if (isAnchor) {
    return (
      <AnchorPriceElement
        className={cx(className, '--anchor-price')}
        data-id={dataId}
        translate="no"
        style={style}
      >
        {formattedNumber}
      </AnchorPriceElement>
    )
  }

  return (
    <PriceWrapper
      className={cx(className, !!shouldAlignOffer && '--with-margin-bottom')}
      data-id={dataId}
      style={style}
    >
      {isAppLocked && value && showAppIcon && (
        <Icon name="Iphone" size="sm" colorPath="content.brand.primary" />
      )}
      {noStyle ? (
        <div translate="no">{formattedNumber}</div>
      ) : (
        <StandardPriceElement
          className={cx(
            `--text-${size}-${variant}`,
            (isPrivate || isAppLocked) && '--special'
          )}
          style={style}
          translate="no"
        >
          {formattedNumber}
        </StandardPriceElement>
      )}
    </PriceWrapper>
  )
}
